import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Trash2, Upload } from 'lucide-react';
import api from '@/lib/api';
import Avatar from '@/components/Avatar';
import { erroDaApi } from '@/lib/erroDaApi';

/**
 * Enviar, trocar e remover a foto na hora, sem esperar o Salvar do formulário —
 * como o logo do fornecedor. Serve ao Meu perfil e ao cadastro de usuários.
 */
export default function FotoEditavel({
  nome,
  fotoUrl,
  rota,
  aoMudar,
  podeEditar = true,
}: {
  nome: string;
  fotoUrl: string | null;
  rota: string;
  aoMudar: (dados: unknown) => void;
  podeEditar?: boolean;
}) {
  const entrada = useRef<HTMLInputElement>(null);

  const enviar = useMutation({
    mutationFn: async (arquivo: File) => {
      const corpo = new FormData();
      corpo.append('foto', arquivo);

      return (await api.post<{ data: unknown }>(rota, corpo)).data.data;
    },
    onSuccess: aoMudar,
  });

  const remover = useMutation({
    mutationFn: async () => (await api.delete<{ data: unknown }>(rota)).data.data,
    onSuccess: aoMudar,
  });

  const erro = enviar.error ?? remover.error;
  const ocupado = enviar.isPending || remover.isPending;

  return (
    <div className="flex items-center gap-md">
      <Avatar nome={nome || '?'} fotoUrl={fotoUrl} tamanho="lg" />
      {podeEditar && (
        <div>
          <div className="flex flex-wrap gap-sm">
            <button
              type="button"
              disabled={ocupado}
              onClick={() => entrada.current?.click()}
              className="inline-flex h-8 items-center gap-xs rounded-md border border-border px-sm text-[13px] font-bold text-ink hover:bg-surface-subtle disabled:opacity-50"
            >
              <Upload size={14} />
              {enviar.isPending ? 'Enviando…' : fotoUrl ? 'Trocar foto' : 'Enviar foto'}
            </button>
            {fotoUrl && (
              <button
                type="button"
                disabled={ocupado}
                onClick={() => remover.mutate()}
                className="inline-flex h-8 items-center gap-xs rounded-md px-sm text-[13px] font-bold text-muted hover:bg-danger/10 hover:text-danger disabled:opacity-50"
              >
                <Trash2 size={14} />
                Remover
              </button>
            )}
          </div>
          <p className="mt-xs text-[12px] text-muted">PNG, JPG ou WEBP, até 2 MB.</p>
          {erro && <p className="mt-xs text-[13px] font-bold text-danger">{erroDaApi(erro, 'Não foi possível salvar a foto.')}</p>}
          <input
            ref={entrada}
            type="file"
            accept=".png,.jpg,.jpeg,.webp"
            className="hidden"
            onChange={(evento) => {
              const arquivo = evento.target.files?.[0];
              if (arquivo) {
                enviar.mutate(arquivo);
              }
              evento.target.value = '';
            }}
          />
        </div>
      )}
    </div>
  );
}
